import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { getLookbooks, getOpportunities, getBrandLists, getBrand } from '../../Helpers/user_helpers';
import { getGiftingRequestsForLookbook, getOpportunityRequestsForOpportunity } from '../../Helpers/brand_helpers';
import { getDiscoverListsForBrand } from '../../APIClient/discover';
import { getOptionsForTalentFiltering } from '../../Helpers/talent_helpers';

export const useUserListOptions = ({ user, analytics }) => {
  const [discoverLists, setDiscoverLists] = useState([]);
  const lookbooks = getLookbooks(user);
  const opportunities = getOpportunities(user);
  const brand = getBrand(user);

  useEffect(() => {
    if (brand) {
      getDiscoverListsForBrand(brand).then(resp => setDiscoverLists(resp));
    }
  }, []);

  // Allow filtering by the same filters used on talent discovery, except filters involving promoters
  const filterOptions = getOptionsForTalentFiltering()
    .filter(({ label }) => label !== 'Promoters')
    .map(({ label, options }) =>
      label === 'Gifting' ? { label, options: options.filter(({ value }) => value !== 'gifted_not_promoting') } : { label, options }
    );
  const listOptions = useMemo(
    () => [
      {
        options: [{ value: null, label: 'All Promoters' }]
      },
      ...filterOptions,
      {
        label: 'Your Lists',
        options: [
          ...getBrandLists(user).map(list => ({
            label: list.title || 'Unknown',
            sublabel: `${list.users.length} creator${list.users.length === 1 ? '' : 's'}`,
            value: {
              BrandList_id: list.id, // Required for API call
              ...list
            }
          }))
        ]
      },
      ...(!discoverLists.length
        ? []
        : [
            {
              label: 'Curated By ShopMy',
              options: discoverLists.map(list => ({
                label: list.title || 'Unknown',
                sublabel: `${list.num_users} creator${list.num_users === 1 ? '' : 's'}`,
                value: {
                  DiscoverList_id: list.id, // Required for API call
                  ...list
                }
              }))
            }
          ]),
      ...(!opportunities?.length
        ? []
        : [
            {
              label: 'Opportunities',
              options: opportunities.map(opportunity => {
                const requests = getOpportunityRequestsForOpportunity(analytics, opportunity);
                const acceptedRequests = _.filter(requests, 'userAccepted');
                return {
                  label: opportunity.title || 'Unknown',
                  sublabels: [`${acceptedRequests.length} requests accepted`],
                  value: {
                    Opportunity_id: opportunity.id, // Required for API call
                    ...opportunity
                  }
                };
              })
            }
          ]),
      ...(!lookbooks?.length
        ? []
        : [
            {
              label: 'Lookbooks',
              options: lookbooks.map(lookbook => {
                const requests = getGiftingRequestsForLookbook(analytics, lookbook);
                const acceptedRequests = _.filter(requests, 'userAccepted');
                return {
                  label: lookbook.title || 'Unknown',
                  sublabels: [`${acceptedRequests.length} requests accepted`],
                  value: {
                    Lookbook_id: lookbook.id, // Required for API call
                    ...lookbook
                  }
                };
              })
            }
          ])
    ],
    [discoverLists]
  );

  const getOptionForValue = value => {
    const defaultOption = listOptions[0].options[0];
    const options = listOptions.flatMap(section => section.options);
    if (typeof value === 'string') return options.find(option => option.value === value) ?? defaultOption;
    if (value?.BrandList_id) return options.find(({ value: v }) => v?.BrandList_id === value.BrandList_id) ?? defaultOption;
    if (value?.DiscoverList_id) return options.find(({ value: v }) => v?.DiscoverList_id === value.DiscoverList_id) ?? defaultOption;
    if (value?.Opportunity_id) return options.find(({ value: v }) => v?.Opportunity_id === value.Opportunity_id) ?? defaultOption;
    if (value?.Lookbook_id) return options.find(({ value: v }) => v?.Lookbook_id === value.Lookbook_id) ?? defaultOption;
    return defaultOption;
  };

  return { listOptions, getOptionForValue };
};
